import React from 'react';
import { ChevronLeft, ChevronRight, Library, FolderOpen, Layers, BookOpen, Settings } from 'lucide-react';
import { ThemeToggle } from './ThemeToggle';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  currentView: 'library' | 'workspace' | 'subgroupings' | 'journalEntries' | 'mAdjustments' | 'settings';
  onNavigateToLibrary: () => void;
  onNavigateToWorkspace: () => void;
  onNavigateToSubGroupings: () => void;
  onNavigateToJournalEntries: () => void;
  onNavigateToMAdjustments: () => void;
  onNavigateToSettings: () => void;
  hasActiveEngagement: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  onToggle,
  currentView,
  onNavigateToLibrary,
  onNavigateToWorkspace,
  onNavigateToSubGroupings,
  onNavigateToJournalEntries,
  onNavigateToMAdjustments,
  onNavigateToSettings,
  hasActiveEngagement,
}) => {
  return (
    <div
    className={`fixed left-0 top-0 h-screen text-white transition-all duration-300 flex flex-col z-30 
      ${collapsed ? 'w-16' : 'w-56'} 
      bg-charcoal-900 dark:bg-slate-950 border-r border-white/5`}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        {!collapsed && (
          <h1 className="text-xl font-bold tracking-tight text-white">TaxTrail</h1>
        )}
        <button
          onClick={onToggle}
          className="p-1.5 hover:bg-white/10 rounded transition-colors ml-auto"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronRight className="w-5 h-5 text-white" />
          ) : (
            <ChevronLeft className="w-5 h-5 text-white" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1">
        {/* Library */}
        <button
          onClick={onNavigateToLibrary}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded transition-colors text-white ${
            currentView === 'library'
              ? 'bg-white/10'
              : 'hover:bg-white/5'
          } ${collapsed ? 'justify-center' : ''}`}
          title={collapsed ? 'Library' : undefined}
        >
          <Library className="w-5 h-5 flex-shrink-0 text-white" />
          {!collapsed && <span className="font-semibold text-white">Library</span>}
        </button>

        {/* Workspace - Only show if there's an active engagement */}
        {hasActiveEngagement && (
          <button
            onClick={onNavigateToWorkspace}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded transition-colors text-white ${
              currentView === 'workspace'
                ? 'bg-white/10'
                : 'hover:bg-white/5'
            } ${collapsed ? 'justify-center' : ''}`}
            title={collapsed ? 'Workspace' : undefined}
          >
            <FolderOpen className="w-5 h-5 flex-shrink-0 text-white" />
            {!collapsed && <span className="font-semibold text-white">Workspace</span>}
          </button>
        )}

        {/* Engagement-scoped tools */}
        <div className="pt-4 mt-4 border-t border-white/10 space-y-1">
          {/* Journal Entries — Active when engagement is open */}
          {hasActiveEngagement ? (
            <button
              onClick={onNavigateToJournalEntries}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded transition-colors text-white ${
                currentView === 'journalEntries' ? 'bg-white/10' : 'hover:bg-white/5'
              } ${collapsed ? 'justify-center' : ''}`}
              title={collapsed ? 'Journal Entries' : undefined}
            >
              <BookOpen className="w-5 h-5 flex-shrink-0 text-white" />
              {!collapsed && <span className="text-sm font-semibold text-white">Journal Entries</span>}
            </button>
          ) : (
            <div
              className={`flex items-center gap-3 px-3 py-2.5 rounded opacity-50 cursor-not-allowed text-white ${
                collapsed ? 'justify-center' : ''
              }`}
              title={collapsed ? 'Open an engagement first' : undefined}
            >
              <BookOpen className="w-5 h-5 flex-shrink-0 text-white" />
              {!collapsed && <span className="text-sm text-white/60">Journal Entries</span>}
            </div>
          )}
          {/* M-Adjustments */}
          {hasActiveEngagement ? (
            <button
              onClick={onNavigateToMAdjustments}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded transition-colors text-white ${
                currentView === 'mAdjustments' ? 'bg-white/10' : 'hover:bg-white/5'
              } ${collapsed ? 'justify-center' : ''}`}
              title={collapsed ? 'M-Adjustments' : undefined}
            >
              <span className="text-xl flex-shrink-0 leading-none text-white">#</span>
              {!collapsed && <span className="text-sm font-semibold text-white">M-Adjustments</span>}
            </button>
          ) : (
            <div
              className={`flex items-center gap-3 px-3 py-2.5 rounded opacity-50 cursor-not-allowed text-white ${
                collapsed ? 'justify-center' : ''
              }`}
              title={collapsed ? 'Open an engagement first' : undefined}
            >
              <span className="text-xl text-white">#</span>
              {!collapsed && <span className="text-sm text-white/60">M-Adjustments</span>}
            </div>
          )}

          {/* SubGroupings */}
          {hasActiveEngagement ? (
            <button
              onClick={onNavigateToSubGroupings}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded transition-colors text-white ${
                currentView === 'subgroupings'
                  ? 'bg-white/10'
                  : 'hover:bg-white/5'
              } ${collapsed ? 'justify-center' : ''}`}
              title={collapsed ? 'SubGroupings' : undefined}
            >
              <Layers className="w-5 h-5 flex-shrink-0 text-white" />
              {!collapsed && <span className="text-sm font-semibold text-white">SubGroupings</span>}
            </button>
          ) : (
            <div
              className={`flex items-center gap-3 px-3 py-2.5 rounded opacity-50 cursor-not-allowed text-white ${
                collapsed ? 'justify-center' : ''
              }`}
              title={collapsed ? 'Open an engagement first' : undefined}
            >
              <Layers className="w-5 h-5 flex-shrink-0 text-white" />
              {!collapsed && <span className="text-sm text-white/60">SubGroupings</span>}
            </div>
          )}

          <div
            className={`flex items-center gap-3 px-3 py-2.5 rounded opacity-50 cursor-not-allowed text-white ${
              collapsed ? 'justify-center' : ''
            }`}
            title={collapsed ? 'Coming soon' : undefined}
          >
            <span className="text-xl text-white">#</span>
            {!collapsed && <span className="text-sm text-white/60">Reconciliation</span>}
          </div>
        </div>

        <div className="pt-4 mt-4 border-t border-white/10">
          {hasActiveEngagement ? (
            <button
              onClick={onNavigateToSettings}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded transition-colors text-white ${
                currentView === 'settings' ? 'bg-white/10' : 'hover:bg-white/5'
              } ${collapsed ? 'justify-center' : ''}`}
              title={collapsed ? 'Settings' : undefined}
            >
              <Settings className="w-5 h-5 flex-shrink-0 text-white" />
              {!collapsed && <span className="text-sm font-semibold text-white">Settings</span>}
            </button>
          ) : (
            <div
              className={`flex items-center gap-3 px-3 py-2.5 rounded opacity-50 cursor-not-allowed text-white ${
                collapsed ? 'justify-center' : ''
              }`}
              title={collapsed ? 'Open an engagement first' : undefined}
            >
              <Settings className="w-5 h-5 flex-shrink-0 text-white" />
              {!collapsed && <span className="text-sm text-white/60">Settings</span>}
            </div>
          )}
        </div>
      </nav>

      {/* Theme Toggle and Footer */}
      <div className="border-t border-white/10">
        {/* Theme Toggle */}
        <div className={`flex items-center ${collapsed ? 'py-3 px-0 justify-center' : 'p-4 gap-3'}`}>
          {!collapsed && (
            <span className="text-sm font-medium text-white/80">Theme</span>
          )}
          <ThemeToggle />
        </div>

        {/* Footer */}
        {!collapsed && (
          <div className="px-4 pb-4 text-xs text-white/60">
            Phase 6: M-Adjustments
          </div>
        )}
      </div>
    </div>
  );
};
